/**
 * DI-3-D (N8) — the runner runs in ONE tab of this browser: the claim
 * protocol, in one file (story §2.2 / §4).
 *
 *   meta.runnerClaim = { tabId, at }       IndexedDB, the `meta` store
 *   BroadcastChannel("aeriosiq-intake")    heartbeat · released · changed
 *
 * The first tab to claim runs the runner; it writes `at` every 5 s
 * (`HEARTBEAT_MS`) and broadcasts a heartbeat. Every other tab is an OBSERVER:
 * it shows the board, says "another tab of this browser is working on this
 * session", and checks the claim every 5 s. A claim whose `at` is older than
 * 15 s (`STALE_MS`) is stale — the tab closed, crashed or slept — and the next
 * tab that checks takes it over. A tab that closes cleanly (`pagehide`)
 * releases its claim and broadcasts `released`, so an observer takes over at
 * once instead of after 15 s. A tab that finds a FRESH claim asks first
 * (`ping`): a live owner answers (`pong`) at once; no answer within 1 s
 * (`PING_WAIT_MS`) means the holder is gone — a reload whose `pagehide` write
 * did not land — and the claim is taken. Without a channel only the age counts.
 *
 * The claim is taken in ONE readwrite transaction (`updateMeta`): IndexedDB
 * serialises readwrite transactions on a store, so two tabs racing cannot
 * both win. The BroadcastChannel only makes observers look sooner; the
 * IndexedDB row is the truth. Where either is missing (a private window,
 * jsdom) the tab claims alone — the protocol degrades to "this tab runs".
 */
import { getMeta, updateMeta } from "../session/db.js";

export const CLAIM_KEY = "runnerClaim";
export const CHANNEL_NAME = "aeriosiq-intake";
export const HEARTBEAT_MS = 5_000;
export const STALE_MS = 15_000;
export const PING_WAIT_MS = 1_000;

export interface RunnerClaimRecord {
  tabId: string;
  at: number;
  /**
   * DI-3-S8-FE — written by `takeOver()` only (the upload lane's steal, never
   * the drop slot's): the tab the claim was taken from. Informational — the
   * owner's next heartbeat yields on the foreign `tabId` alone; the stealer's
   * first heartbeat rewrites the row without it.
   */
  stolenFrom?: string;
}

export type ClaimMessage =
  | { type: "heartbeat"; tabId: string; at: number }
  | { type: "released"; tabId: string }
  | { type: "changed"; tabId: string; sessionId: string | null }
  | { type: "ping"; tabId: string }
  | { type: "pong"; tabId: string; to: string };

export interface ChannelLike {
  postMessage: (m: ClaimMessage) => void;
  close: () => void;
  onmessage: ((e: { data: ClaimMessage }) => void) | null;
}

export interface ClaimDeps {
  tabId?: string;
  now?: () => number;
  /** null = no BroadcastChannel in this browser. */
  channel?: () => ChannelLike | null;
  setInterval?: (fn: () => void, ms: number) => unknown;
  clearInterval?: (id: unknown) => void;
  /** Resolves after `ms` (the ping's wait). */
  wait?: (ms: number) => Promise<void>;
  read?: () => Promise<RunnerClaimRecord | undefined>;
  update?: (
    fn: (cur: RunnerClaimRecord | undefined) => RunnerClaimRecord | undefined,
  ) => Promise<RunnerClaimRecord | undefined>;
}

export type TabRole = "idle" | "owner" | "observer";

function newTabId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function")
    return crypto.randomUUID();
  return `tab-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function defaultChannel(): ChannelLike | null {
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    const ch = new BroadcastChannel(CHANNEL_NAME);
    // Node's BroadcastChannel (a test runner) keeps the process alive otherwise
    (ch as unknown as { unref?: () => void }).unref?.();
    return ch as unknown as ChannelLike;
  } catch {
    return null;
  }
}

/** Is `claim` free for `tabId` at `now`? (absent, ours, or stale) */
export function claimable(
  claim: RunnerClaimRecord | undefined,
  tabId: string,
  now: number,
): boolean {
  return !claim || claim.tabId === tabId || now - claim.at > STALE_MS;
}

export class RunnerClaim {
  readonly tabId: string;
  private readonly now: () => number;
  private readonly setIntervalFn: (fn: () => void, ms: number) => unknown;
  private readonly clearIntervalFn: (id: unknown) => void;
  private readonly read: () => Promise<RunnerClaimRecord | undefined>;
  private readonly update: NonNullable<ClaimDeps["update"]>;
  private readonly channel: ChannelLike | null;
  private timer: unknown = null;
  private roleValue: TabRole = "idle";
  private readonly listeners = new Set<(tabRole: TabRole) => void>();
  private readonly changeListeners = new Set<
    (sessionId: string | null) => void
  >();
  private checking = false;
  private readonly wait: (ms: number) => Promise<void>;
  private ponged = false;

  constructor(deps: ClaimDeps = {}) {
    this.tabId = deps.tabId ?? newTabId();
    this.now = deps.now ?? (() => Date.now());
    this.setIntervalFn =
      deps.setInterval ?? ((fn, ms) => globalThis.setInterval(fn, ms));
    this.clearIntervalFn =
      deps.clearInterval ??
      ((id) => {
        globalThis.clearInterval(id as ReturnType<typeof setInterval>);
      });
    this.read = deps.read ?? (() => getMeta<RunnerClaimRecord>(CLAIM_KEY));
    this.update =
      deps.update ?? ((fn) => updateMeta<RunnerClaimRecord>(CLAIM_KEY, fn));
    this.wait =
      deps.wait ??
      ((ms) =>
        new Promise((r) => {
          globalThis.setTimeout(r, ms);
        }));
    this.channel = (deps.channel ?? defaultChannel)();
    if (this.channel) {
      this.channel.onmessage = (e) => {
        this.onMessage(e.data);
      };
    }
  }

  get role(): TabRole {
    return this.roleValue;
  }

  /** Called with the new role whenever it changes (observer → owner = a takeover). */
  onRole(fn: (tabRole: TabRole) => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  /** Called when the owner says the session changed (observers re-read it). */
  onChanged(fn: (sessionId: string | null) => void): () => void {
    this.changeListeners.add(fn);
    return () => this.changeListeners.delete(fn);
  }

  /**
   * Try to become the owner. `force` takes the claim whatever its age — the
   * pilot pressed Start in THIS tab. Resolves to the role afterwards.
   */
  async claim(force = false): Promise<TabRole> {
    if (!force && this.channel) {
      const cur = await this.read().catch(() => undefined);
      if (cur && !claimable(cur, this.tabId, this.now())) {
        // a fresh claim of another tab: is that tab still there?
        this.ponged = false;
        this.post({ type: "ping", tabId: this.tabId });
        await this.wait(PING_WAIT_MS);
        if (!this.answered()) force = true; // nobody answered: the holder is gone
      }
    }
    const now = this.now();
    const stored = await this.update((cur) =>
      force || claimable(cur, this.tabId, now)
        ? { tabId: this.tabId, at: now }
        : undefined,
    ).catch(() => undefined);
    // no IndexedDB → nothing to share with: this tab runs
    const won = stored === undefined || stored.tabId === this.tabId;
    this.setRole(won ? "owner" : "observer");
    this.startTimer();
    if (won) this.post({ type: "heartbeat", tabId: this.tabId, at: now });
    return this.roleValue;
  }

  /**
   * DI-3-S8-FE — "Take over in this tab": the UPLOAD LANE's steal. Never
   * called by the drop slot (its rules stay as they are: `claim(force)` on
   * Start, the stale rule, the ping). Writes the claim with this tab's id
   * whatever its age, and `stolenFrom` = the tab that held it while it was
   * fresh. That tab's next heartbeat (`tick`, ≤ `HEARTBEAT_MS`) reads a
   * foreign id and steps back to observer — the caller (the lane) stops its
   * driver on that role change. Resolves to the tab the claim was taken from,
   * or null when it was free (absent, released, stale, or already ours):
   * then there is nobody to wait for.
   */
  async takeOver(): Promise<string | null> {
    const now = this.now();
    // set inside the transaction's callback (the row it read)
    const prev: { tabId: string | null } = { tabId: null };
    const stored = await this.update((cur) => {
      prev.tabId = cur && !claimable(cur, this.tabId, now) ? cur.tabId : null;
      return prev.tabId === null
        ? { tabId: this.tabId, at: now }
        : { tabId: this.tabId, at: now, stolenFrom: prev.tabId };
    }).catch(() => undefined);
    const won = stored === undefined || stored.tabId === this.tabId;
    this.setRole(won ? "owner" : "observer");
    this.startTimer();
    return won ? prev.tabId : null;
  }

  /** The owner tells the observers to re-read the session record. */
  changed(sessionId: string | null): void {
    this.post({ type: "changed", tabId: this.tabId, sessionId });
  }

  /** A clean close: free the claim at once (observers take over without waiting 15 s). */
  async release(): Promise<void> {
    this.stopTimer();
    if (this.roleValue !== "owner") {
      this.setRole("idle");
      return;
    }
    this.setRole("idle");
    await this.update((cur) =>
      cur?.tabId === this.tabId ? { tabId: this.tabId, at: 0 } : undefined,
    ).catch(() => undefined);
    this.post({ type: "released", tabId: this.tabId });
  }

  /** Tests / teardown: stop the timer and close the channel. */
  dispose(): void {
    this.stopTimer();
    this.channel?.close();
    this.listeners.clear();
    this.changeListeners.clear();
  }

  /** One beat: the owner refreshes `at`; an observer checks for a stale claim. */
  async tick(): Promise<void> {
    if (this.checking) return;
    this.checking = true;
    try {
      if (this.roleValue === "owner") {
        const now = this.now();
        const stored = await this.update((cur) =>
          !cur || cur.tabId === this.tabId
            ? { tabId: this.tabId, at: now }
            : undefined,
        ).catch(() => undefined);
        if (stored && stored.tabId !== this.tabId) {
          // another tab took over (a forced Start there): this one steps back
          this.setRole("observer");
          return;
        }
        this.post({ type: "heartbeat", tabId: this.tabId, at: now });
        return;
      }
      if (this.roleValue === "observer") {
        const cur = await this.read().catch(() => undefined);
        if (claimable(cur, this.tabId, this.now())) await this.claim();
      }
    } finally {
      this.checking = false;
    }
  }

  private onMessage(m: ClaimMessage): void {
    if (m.tabId === this.tabId) return;
    if (m.type === "ping") {
      if (this.roleValue === "owner")
        this.post({ type: "pong", tabId: this.tabId, to: m.tabId });
      return;
    }
    if (m.type === "pong") {
      if (m.to === this.tabId) this.ponged = true;
      return;
    }
    if (m.type === "released" && this.roleValue === "observer") {
      void this.tick();
      return;
    }
    if (m.type === "heartbeat" && this.roleValue === "owner") {
      // two owners (a race lost to a forced claim): the row decides
      void this.tick();
      return;
    }
    if (m.type === "changed") {
      for (const fn of this.changeListeners) fn(m.sessionId);
    }
  }

  /** Did the holder answer the ping? (a method: set from the channel's callback) */
  private answered(): boolean {
    return this.ponged;
  }

  private setRole(tabRole: TabRole): void {
    if (tabRole === this.roleValue) return;
    this.roleValue = tabRole;
    for (const fn of this.listeners) fn(tabRole);
  }

  private startTimer(): void {
    if (this.timer !== null) return;
    this.timer = this.setIntervalFn(() => {
      void this.tick();
    }, HEARTBEAT_MS);
  }

  private stopTimer(): void {
    if (this.timer === null) return;
    this.clearIntervalFn(this.timer);
    this.timer = null;
  }

  private post(m: ClaimMessage): void {
    try {
      this.channel?.postMessage(m);
    } catch {
      /* a closed channel: the IndexedDB row is the truth anyway */
    }
  }
}
